import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useO5AwsDeployerV1ClusterQueryServiceClusterGet } from '@interxfi/api-client/generated/hooks/o5-aws-deployer-v1-cluster-query-service-cluster-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Typography } from '@interxfi/design-system/components/typography';
import type { O5AwsDeployerV1ClusterState } from '@interxfi/types/generated/api';
import { O5ClusterEntityMenu } from '../../../../../../../features/o5/cluster/o5-cluster-entity-menu';
import { O5ClusterEventSummary } from '../../../../../../../features/o5/cluster/o5-cluster-event';
import { getO5ClusterStateListItems, type getO5EnvironmentClusterListItems } from '../../../../../../../features/o5/cluster/o5-cluster-list-items';
import { getO5ClusterDetailPath } from '../../../../../../../features/o5/cluster/o5-cluster-routing';
import { O5ClusterStatusPill } from '../../../../../../../features/o5/cluster/o5-cluster-status-pill';
import { O5EnvironmentRdsHostsTable } from '../../../../../../../features/o5/cluster/o5-environment-rds-hosts-table';
import { O5ParameterOverridesTable } from '../../../../../../../features/o5/cluster/o5-parameter-overrides-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

function ClusterBreadcrumb({ clusterId }: { clusterId: string }) {
  const { data, error, isPending } = useO5AwsDeployerV1ClusterQueryServiceClusterGet({ clusterId });
  const showPending = Boolean(isPending || error);

  if (showPending) {
    return <Skeleton charCount={8} />;
  }

  return data?.cluster?.data?.config?.name || data?.cluster?.data?.baseConfig?.name || `Cluster ${getShortenedId(clusterId)}`;
}

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/cluster/_cluster/$clusterId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `o5-cluster-${params.clusterId}`,
        label: <ClusterBreadcrumb clusterId={params.clusterId} />,
        to: getO5ClusterDetailPath(params.clusterId),
        suffix: <CopyButton content={params.clusterId} label="Copy Cluster ID" tooltip={{ copy: params.clusterId, copied: 'Copied Cluster ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Cluster'),
  component: O5ClusterDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load cluster', 'view this cluster');

function getConfigGroups(items: ReturnType<typeof getO5EnvironmentClusterListItems>): ReadonlyListGroup[] {
  return [
    {
      id: 'main',
      title: 'Config',
      items: [
        items.name,
        ...('aws' in items.provider
          ? [
              { ...items.provider.aws.value.awsAccount, title: 'AWS Account' },
              { ...items.provider.aws.value.awsRegion, title: 'AWS Region' },
              items.provider.aws.value.globalNamespace,
              items.provider.aws.value.vpcId,
            ]
          : []),
      ],
    },
    ...('aws' in items.provider && items.provider.aws.value.albIngress
      ? [
          {
            id: 'albIngress',
            title: 'ALB Ingress',
            items: [items.provider.aws.value.albIngress.listenerArn, items.provider.aws.value.albIngress.securityGroupId],
          },
        ]
      : []),
    ...('aws' in items.provider && items.provider.aws.value.compute && 'ecs' in items.provider.aws.value.compute
      ? [
          {
            id: 'ecsCluster',
            title: 'ECS Cluster',
            items: [
              items.provider.aws.value.compute.ecs.value.clusterName,
              items.provider.aws.value.compute.ecs.value.baseSecurityGroupId,
              items.provider.aws.value.compute.ecs.value.defaultRegistry,
              items.provider.aws.value.compute.ecs.value.taskExecutionRole,
              items.provider.aws.value.compute.ecs.value.subnetIds,
            ],
          },
        ]
      : []),
    ...('aws' in items.provider && items.provider.aws.value.compute && 'eks' in items.provider.aws.value.compute
      ? [
          {
            id: 'eksCluster',
            title: 'EKS Cluster',
            items: [items.provider.aws.value.compute.eks.value.clusterName, items.provider.aws.value.compute.eks.value.defaultRegistry],
          },
        ]
      : []),
    ...('aws' in items.provider && items.provider.aws.value.eventBridge
      ? [
          {
            id: 'eventBridge',
            title: 'Event Bridge',
            items: [items.provider.aws.value.eventBridge.eventBusArn],
          },
        ]
      : []),
    ...('aws' in items.provider && items.provider.aws.value.o5Deployer
      ? [
          {
            id: 'o5Deployer',
            title: 'O5 Deployer',
            items: [items.provider.aws.value.o5Deployer.assumeRoleArn],
          },
        ]
      : []),
    ...('aws' in items.provider && items.provider.aws.value.o5Sidecar
      ? [
          {
            id: 'o5Sidecar',
            title: 'O5 Sidecar',
            items: [items.provider.aws.value.o5Sidecar.imageRepo, items.provider.aws.value.o5Sidecar.imageVersion],
          },
        ]
      : []),
  ];
}

function getListItems(data: O5AwsDeployerV1ClusterState | undefined) {
  const items = getO5ClusterStateListItems(data);

  return {
    left: getConfigGroups(items.data.config),
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.clusterId] as const satisfies ReadonlyListItem[],
  } as const;
}

function O5ClusterDetail() {
  const { clusterId } = Route.useParams();
  const request = { clusterId } as const;
  const { data, error, isPending } = useO5AwsDeployerV1ClusterQueryServiceClusterGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.cluster), [data?.cluster]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <O5ClusterStatusPill isPending={showPending} size="large" status={data?.cluster?.status} />
          </HorizontalStack>
          <O5ClusterEntityMenu clusterId={clusterId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <O5ClusterEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />

          {data?.cluster?.data?.config?.provider?.aws?.rdsHosts ? (
            <VerticalStack gap={4}>
              <Typography.Level3>RDS Hosts</Typography.Level3>
              <O5EnvironmentRdsHostsTable hosts={data.cluster.data.config.provider?.aws.rdsHosts} />
            </VerticalStack>
          ) : null}
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Overrides</Typography.Level3>

          <O5ParameterOverridesTable overrides={data?.cluster?.data?.overrides || []} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
